import { Logger } from "@devforce/trailhead-client-logger";
export const logger = new Logger({
    prefix: "[@devforce/trailhead-client-page]: ",
});
/**
 * Builds a page by loading modules and appending custom
 * elements to the provided shadowRoot.
 */
export async function buildPage(config, template, options = {}) {
    const areas = new Map();
    const elements = [];
    // Flatten elements so that src requests can run concurrently
    for (const area of config) {
        // Store the DOM node associated with the area
        const node = template.querySelector(`[data-area="${area.id}"]`);
        if (!node) {
            logger.warn(`Area not found: "${area.id}"`);
            continue;
        }
        areas.set(area.id, node);
        // Async build the elements
        for (const pageElement of area.elements) {
            elements.push(buildElement(area.id, pageElement));
        }
    }
    // Wait until all requests are done
    await Promise.allSettled(elements).then((res) => {
        res.forEach((r) => {
            // Log and bail if request fails
            if (r.status === "rejected") {
                logger.error(r.reason);
                return;
            }
            // Append element to area
            const { area, element } = r.value;
            const target = areas.get(area);
            if (target) {
                if (typeof options.willAppendElement === "function") {
                    options.willAppendElement(element);
                }
                target.appendChild(element);
            }
        });
    });
}
/**
 * Builds an element by loading it's dependencies
 * and creating a document node.
 */
export async function buildElement(area, config) {
    // Fetch modules
    try {
        for await (let src of [].concat(config.src))
            await import(src);
    }
    catch (error) {
        throw new Error(`Could not load ${config.tagname}. ${error}`);
    }
    // Create element
    const { id, attributes, properties, tagname } = config;
    const element = document.createElement(tagname);
    // Set attributes if they've been provided
    if (attributes) {
        Object.keys(attributes).forEach((qualifiedName) => {
            element.setAttribute(qualifiedName, attributes[qualifiedName]);
        });
    }
    // Set properties if they've been provided
    if (properties) {
        Object.assign(element, properties);
    }
    // Set id for landmarking
    element.id = id;
    return {
        area,
        element,
    };
}
export const DEFAULT_PAGE_ADAPTER_VALUE = {
    loading: false,
    loaded: false,
};
export class PageAdapter {
    constructor(setValue) {
        this.loading = DEFAULT_PAGE_ADAPTER_VALUE.loading;
        this.loaded = DEFAULT_PAGE_ADAPTER_VALUE.loaded;
        this.setValue = setValue;
    }
    connect() {
        this.setValue({ loading: this.loading, loaded: this.loaded });
    }
    disconnect() { }
    update({ getConfig, template, willAppendElement }) {
        if (this.loading || this.loaded)
            return;
        const start = new Date();
        this.loading = true;
        this.setValue({ loading: this.loading, loaded: this.loaded });
        getConfig()
            .then((config) => buildPage(config, template, { willAppendElement }))
            .then(() => {
            logger.info(`Page built in ${new Date().getTime() - start.getTime()}ms`);
        })
            .catch((e) => {
            logger.error("Could not load page configuration.");
            logger.error(e);
        })
            .finally(() => {
            this.loading = false;
            this.loaded = true;
            this.setValue({ loading: this.loading, loaded: this.loaded });
        });
    }
}
